// The chat panel's transcript, built from the assistant's streamed events (a live turn) or from the
// stored messages of a session (history). Pure, so it is unit-tested without a browser. The chat's React views
// (through @cortex/sdk-chat-client) and an app's own chat UI (through @cortex/app-sdk/chat) read the same model.

export interface Block {
  type: string;
  [key: string]: unknown;
}

export interface ToolChip {
  id: string;
  agent: string;
  name: string;
  status: 'running' | 'done' | 'failed';
  summary?: string;
  ms?: number;
}

export type Item =
  | { kind: 'user'; id: string; text: string }
  | {
      kind: 'assistant';
      id: string;
      text: string;
      /** Streaming until the turn's `done`. */
      complete: boolean;
      tools: ToolChip[];
      blocks: Block[];
      /** The persona working right now (a delegate's progress). */
      working: string | null;
      status?: 'ok' | 'refused' | 'error' | 'stopped';
      usage?: { inputTokens: number; outputTokens: number; costUsd: number | null };
    }
  | { kind: 'error'; id: string; text: string };

export type ChatEvent =
  | { type: 'session'; sessionId: string; title: string }
  | {
      type: 'run_start';
      agent: string;
      label: string;
      task: string;
      parent: string | null;
      /** A delegated run: 'fork' read the conversation before its task, 'isolated' the task alone. */
      context?: 'fork' | 'isolated';
    }
  | { type: 'tool_call'; agent: string; id: string; name: string; input: Record<string, unknown> }
  | {
      type: 'tool_result';
      agent: string;
      id: string;
      name: string;
      status: 'done' | 'failed';
      summary: string;
      ms: number;
    }
  | { type: 'block'; agent: string; block: Block }
  | {
      type: 'run_end';
      agent: string;
      status: string;
      guardrail: unknown;
      inputTokens: number;
      outputTokens: number;
    }
  | { type: 'text'; delta: string }
  | {
      type: 'done';
      turnId: string;
      status: 'ok' | 'refused' | 'error' | 'stopped';
      text: string;
      blocks: Block[];
      usage: { inputTokens: number; outputTokens: number; costUsd: number | null };
      recorded: boolean;
    }
  | { type: 'error'; message: string; status: number };

let n = 0;
const uid = (p: string) => `${p}${Date.now().toString(36)}${(n++).toString(36)}`;

/** The items of stored messages (a session reopened from history). */
export function fromHistory(
  messages: { id: number; role: 'user' | 'assistant'; content: string; blocks: Block[] }[],
): Item[] {
  return messages.map((m) =>
    m.role === 'user'
      ? { kind: 'user', id: `m${m.id}`, text: m.content }
      : {
          kind: 'assistant',
          id: `m${m.id}`,
          text: m.content,
          complete: true,
          tools: [],
          blocks: m.blocks,
          working: null,
        },
  );
}

/** A new turn: the user's message and an empty, streaming answer. */
export function startTurn(items: Item[], text: string): Item[] {
  return [
    ...items,
    { kind: 'user', id: uid('u'), text },
    { kind: 'assistant', id: uid('a'), text: '', complete: false, tools: [], blocks: [], working: null },
  ];
}

/** Applies one streamed event to the turn in progress (the last assistant item). */
export function applyEvent(items: Item[], e: ChatEvent): Item[] {
  if (e.type === 'error') {
    const out = items.map((it, i) =>
      i === items.length - 1 && it.kind === 'assistant' && !it.complete
        ? { ...it, complete: true, working: null, status: 'error' as const }
        : it,
    );
    return [...out, { kind: 'error', id: uid('e'), text: e.message }];
  }
  const last = items.at(-1);
  if (!last || last.kind !== 'assistant' || last.complete) return items;
  const next = { ...last };
  switch (e.type) {
    case 'run_start':
      next.working = e.parent ? e.label : next.working;
      break;
    case 'run_end':
      if (e.agent !== 'orchestrator') next.working = null;
      break;
    case 'tool_call':
      next.tools = [...next.tools, { id: e.id, agent: e.agent, name: e.name, status: 'running' }];
      break;
    case 'tool_result':
      next.tools = next.tools.map((t) =>
        t.id === e.id ? { ...t, status: e.status, summary: e.summary, ms: e.ms } : t,
      );
      break;
    case 'block':
      next.blocks = [...next.blocks, e.block];
      break;
    case 'text':
      next.text += e.delta;
      break;
    case 'done':
      next.text = e.text;
      next.blocks = e.blocks;
      next.complete = true;
      next.working = null;
      next.status = e.status;
      next.usage = e.usage;
      break;
    default:
      return items;
  }
  return [...items.slice(0, -1), next];
}

/** Ends a turn the person stopped (the stream was cut). */
export function stopTurn(items: Item[]): Item[] {
  const last = items.at(-1);
  if (!last || last.kind !== 'assistant' || last.complete) return items;
  return [
    ...items.slice(0, -1),
    {
      ...last,
      complete: true,
      working: null,
      status: 'stopped',
      tools: last.tools.map((t) => (t.status === 'running' ? { ...t, status: 'failed' as const } : t)),
    },
  ];
}

/** The events of a server-sent event stream, as they arrive. */
export async function* sseEvents(body: ReadableStream<Uint8Array>): AsyncGenerator<ChatEvent> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let cut: number;
    while ((cut = buf.indexOf('\n\n')) >= 0) {
      const frame = buf.slice(0, cut);
      buf = buf.slice(cut + 2);
      const data = frame
        .split('\n')
        .filter((l) => l.startsWith('data: '))
        .map((l) => l.slice(6))
        .join('\n');
      if (!data) continue;
      try {
        yield JSON.parse(data) as ChatEvent;
      } catch {
        /* a malformed frame is skipped */
      }
    }
  }
}

export const formatUsage = (u: { inputTokens: number; outputTokens: number; costUsd: number | null }) =>
  `in ${u.inputTokens.toLocaleString('en-US')} · out ${u.outputTokens.toLocaleString('en-US')}${
    u.costUsd === null ? '' : ` · $${u.costUsd.toFixed(4)}`
  }`;
